import { createElement, useCallback, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { RouletteWinnerShareCard } from '@/components/RouletteWinnerShareCard'
import { getRouletteWinnerShareCard } from '@/lib/roulette'
import { captureElementToPng, downloadPngBlob } from '@/lib/share-card/domToPng'
import { buildRouletteShareFilename } from '@/lib/share-card/rouletteShareCardCopy'
import { waitForShareCardAssets } from '@/lib/share-card/waitForShareCardAssets'

export function useRouletteWinnerSharePng(token: string | null) {
  const [sharingPrizeId, setSharingPrizeId] = useState<number | null>(null)
  const [shareError, setShareError] = useState<string | null>(null)
  const sharingRef = useRef(false)

  const shareWinnerPng = useCallback(
    async (campaignId: number, prizeId: number): Promise<void> => {
      if (sharingRef.current) {
        return
      }

      sharingRef.current = true
      setSharingPrizeId(prizeId)
      setShareError(null)

      const mountNode = document.createElement('div')
      mountNode.style.position = 'fixed'
      mountNode.style.left = '-99999px'
      mountNode.style.top = '0'
      mountNode.style.pointerEvents = 'none'
      document.body.appendChild(mountNode)

      const root = createRoot(mountNode)

      try {
        const shareCard = await getRouletteWinnerShareCard(campaignId, prizeId, token)
        root.render(createElement(RouletteWinnerShareCard, { data: shareCard }))

        await waitForShareCardAssets(mountNode)

        const cardElement = mountNode.querySelector('[data-share-card-root]')
        if (!(cardElement instanceof HTMLElement)) {
          throw new Error('Não foi possível montar a imagem do ganhador.')
        }

        const blob = await captureElementToPng(cardElement)
        downloadPngBlob(blob, buildRouletteShareFilename(prizeId, shareCard.prize.prize_type))
      } catch {
        setShareError('Não foi possível baixar a imagem.')
      } finally {
        root.unmount()
        mountNode.remove()
        sharingRef.current = false
        setSharingPrizeId(null)
      }
    },
    [token]
  )

  const clearShareError = useCallback(() => {
    setShareError(null)
  }, [])

  return {
    sharingPrizeId,
    shareError,
    shareWinnerPng,
    clearShareError,
  }
}
